import React from 'react';
import { useAuthorization } from '../auth/useAuthorization';
import { Role } from '../auth/roles';
import AccessDenied from '../pages/AccessDenied/AccessDenied';

/**
 * Inline element guard. Renders `children` only when the account holds `role`
 * (Admin always passes); otherwise renders `fallback` (nothing by default).
 *
 *   <Can role={Roles.StepPromptConfig}><PromptConfigButton /></Can>
 */
export const Can: React.FC<{
  role: Role;
  children: React.ReactNode;
  fallback?: React.ReactNode;
}> = ({ role, children, fallback = null }) => {
  const { hasRole } = useAuthorization();
  return <>{hasRole(role) ? children : fallback}</>;
};

/**
 * Route-level guard. Renders `children` when the account holds `role`, otherwise
 * renders the Access Denied page (keeping the URL, so a deep link is not lost).
 * Assumes the caller has already confirmed authentication.
 */
export const PermissionGuard: React.FC<{
  role: Role;
  children: React.ReactElement;
}> = ({ role, children }) => {
  const { hasRole } = useAuthorization();
  return hasRole(role) ? children : <AccessDenied requiredRole={role} />;
};
